import {useEffect, useRef} from 'react';
// Fetches a static checkout-ui asset manifest, not a WAF-protected API.
// eslint-disable-next-line no-restricted-imports
import {httpGetJson} from '@treatwell/ui';

const isPrefetchSupported = () => {
  return (
    'relList' in HTMLLinkElement.prototype &&
    document.createElement('link').relList.supports('prefetch')
  );
};

type ResourceType = 'style' | 'script' | 'fetch';

const getResourceType = (path: string): ResourceType => {
  if (path.endsWith('.css')) {
    return 'style';
  }
  if (path.endsWith('.js')) {
    return 'script';
  }
  return 'fetch';
};

const addPrefetchElement = (path: string, resourceType: ResourceType) => {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = path;
  link.as = resourceType;
  document.head.appendChild(link);
};

const prefetchFallback = (path: string) => {
  return fetch(path, {credentials: 'same-origin'}).catch(() => {
    // ignore fetch errors - this is just progressive enhancement
  });
};

const requestWhenIdle = (callback: () => void) => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(callback, {timeout: 2000});
  } else {
    setTimeout(callback, 1000);
  }
};

/**
 * This component will prefetch vital css/js assets needed to initialise the checkout.
 * The goal here is to give browsers a headstart once datetime-ui has rendered and
 * become interactive, making an assumption that the next thing the user will
 * do is navigate to checkout-ui.
 * For browsers without prefetch support (essentially Safari everywhere),
 * a standard fetch request serves as a fallback, ensuring they also get some
 * benefit

 * The manifest is generated by the checkout-ui webpack and looks a bit like this:

    {
      "main.css": "/checkout-ui/static/css/main.7c0490de.css",
      "main.js": "/checkout-ui/static/js/main.38bdcc98.js",
      "runtime.js": "/checkout-ui/static/js/runtime.4c93cd16.js",
      "vendors~main.css": "/checkout-ui/static/css/vendors~main.01210560.css",
      "vendors~main.js": "/checkout-ui/static/js/vendors~main.6b212154.js"
    }

  * Note: It has no cache and therefore always points to the latest fingerprinted files in
  * production (it works in development too, but is not that useful)
 */
export const PrefetchCheckoutAssets = () => {
  const isPrefetching = useRef(false);

  useEffect(() => {
    const prefetchResources = async () => {
      if (isPrefetching.current) {
        return;
      }
      isPrefetching.current = true;

      try {
        const manifest = await httpGetJson<Record<string, string>>({
          path: '/checkout-ui/webpack-manifest.json',
        });

        for (const path of Object.values(manifest)) {
          const resourceType = getResourceType(path);

          if (isPrefetchSupported()) {
            addPrefetchElement(path, resourceType);
          } else {
            // this is for Safari on all platforms
            // https://caniuse.com/?search=prefetch
            prefetchFallback(path);
          }
        }
      } catch (error) {
        // we don't really care if this fails, as it's just a progressive enhancement
        isPrefetching.current = false;
      }
    };

    requestWhenIdle(prefetchResources);
  }, []);

  return null;
};
